import React, { useRef, useState } from 'react';
import { MdAttachFile } from 'react-icons/md';
import {
    collection,
    addDoc,
    serverTimestamp,
    doc,
    updateDoc,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db } from './firebase';

/**
 * SendDocument component handles file uploads (Images, Videos, Documents)
 * Uses Inline Styles for styling.
 */

const storage = getStorage();

const SendDocument = ({ user, selectedChat, setMessages }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isHovered, setIsHovered] = useState(false); // For button hover effect

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && selectedChat) {
            uploadFile(file);
        }
        e.target.value = null; // Clear input
    };

    const uploadFile = (file) => {
        if (!file || !selectedChat || !user) return;

        setUploading(true);
        setUploadProgress(0);

        const fileExtension = file.name.split('.').pop().toLowerCase();
        let fileType = 'document';
        let messageContent = `File: ${file.name}`;

        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
            fileType = 'image';
            messageContent = `Image`;
        } else if (['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension)) {
            fileType = 'video';
            messageContent = `Video`;
        }

        const storageRef = ref(storage, `chat_files/${selectedChat.id}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setUploadProgress(progress);
            },
            (error) => {
                console.error("Upload failed:", error);
                setUploading(false);
                alert("File upload failed. Please try again.");
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                const messageData = {
                    senderId: user.uid,
                    timestamp: serverTimestamp(),
                    fileUrl: downloadURL,
                    fileName: file.name,
                    fileType: fileType,
                    isGroup: selectedChat.isGroup || false,
                };

                try {
                    const docRef = await addDoc(collection(db, 'chats', selectedChat.id, 'messages'), messageData);
                    
                    // Update last message in chat list
                    const senderProfile = user.displayName || user.email || 'You'; // Fallback
                    const lastMessageText = selectedChat.isGroup
                        ? `${senderProfile}: Sent a ${fileType}`
                        : `Sent a ${fileType}`;

                    await updateDoc(doc(db, 'chats', selectedChat.id), {
                        lastMessage: lastMessageText,
                        lastMessageTimestamp: serverTimestamp(),
                        lastMessageSenderId: user.uid,
                    });

                    // Optimistic update handled by snapshot listener in Messaging.js usually, 
                    // but we ensure clean state here.
                } catch (error) {
                    console.error("Error saving file message:", error);
                } finally {
                    setUploading(false);
                    setUploadProgress(0);
                }
            }
        );
    };

    // --- Inline Styles ---
    const styles = {
        container: {
            display: 'flex',
            alignItems: 'center',
            marginLeft: '8px',
        },
        hiddenInput: {
            display: 'none',
        },
        attachButton: {
            backgroundColor: isHovered ? '#d1d5db' : '#e5e7eb', // gray-300 : gray-200
            color: '#4b5563', // gray-600
            padding: '10px',
            borderRadius: '50%',
            border: 'none',
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: uploading ? 0.6 : 1,
        },
        progressBarContainer: {
            marginLeft: '12px',
            width: '100px',
            height: '6px',
            backgroundColor: '#d1d5db',
            borderRadius: '9999px',
            overflow: 'hidden',
            display: 'flex', // Align internal bar
        },
        progressBarFill: {
            height: '100%',
            backgroundColor: '#3b82f6', // blue-500
            width: `${uploadProgress}%`,
            transition: 'width 0.3s ease-out',
        },
        progressText: {
            marginLeft: '8px',
            fontSize: '0.75rem',
            color: '#2563eb', // blue-600
            fontWeight: '500',
        }
    };

    return (
        <div style={styles.container}>
            <input
                type="file"
                ref={fileInputRef}
                style={styles.hiddenInput}
                accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                disabled={uploading}
                onChange={handleFileChange}
            />
            
            <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                style={styles.attachButton}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                disabled={uploading}
                title={uploading ? `Uploading ${uploadProgress}%...` : "Attach File (Image, Video, Document)"}
            >
                <MdAttachFile size={24} />
            </button>
            
            {/* Progress Bar UI */}
            {uploading && (
                <>
                    <div style={styles.progressBarContainer}>
                        <div style={styles.progressBarFill} />
                    </div>
                    <span style={styles.progressText}>
                        {uploadProgress}%
                    </span>
                </>
            )}
        </div>
    );
};

export default SendDocument;                           